import type { SideId } from '@/domain/anatomy/side';
import type { LumbarLevel } from '@/domain/anatomy/hipSpineLandmarks';

/**
 * Minimal landmark shape the save logic reads — name + current coordinates. Declared
 * here (not imported from `@/api`) so the domain layer stays free of API DTO coupling;
 * the richer `HipSpineLandmarkApiObject` is structurally assignable to it.
 */
export type LandmarkCoordinates = { name: string; current_x: number; current_y: number };

export type LandmarkSavePayload = LandmarkCoordinates;

export type PosturePutBody = {
    side: SideId;
    lumbar_vertebra_end_plate: LumbarLevel;
    femur_1_head_radius: number;
    femur_2_head_radius: number;
};

/** The persisted posture editable fields the save logic compares against. */
export type PersistedPostureFields = PosturePutBody;

export type ResolvedPostureFields = {
    side: SideId;
    lumbarLevel: LumbarLevel;
    femur1: number;
    femur2: number;
};

/**
 * The landmarks whose current_x/current_y differ from the server copy (matched by
 * name), mapped to the PUT body shape { name, current_x, current_y }. Returns [] when
 * nothing changed. Pure; input arrays are not mutated.
 *
 * Exact (===) inequality is intentional: un-edited landmarks pass through the
 * identical floating-point numbers received from the server, so an epsilon
 * comparison would incorrectly suppress edits that happen to be very small.
 */
export function diffLandmarksForSave(
    oriented: LandmarkCoordinates[],
    server: LandmarkCoordinates[],
): LandmarkSavePayload[] {
    const serverByName = new Map(server.map((lm) => [lm.name, lm]));

    return oriented.reduce<LandmarkSavePayload[]>((acc, lm) => {
        const serverLm = serverByName.get(lm.name);
        if (!serverLm) return acc;
        if (lm.current_x !== serverLm.current_x || lm.current_y !== serverLm.current_y) {
            acc.push({ name: lm.name, current_x: lm.current_x, current_y: lm.current_y });
        }
        return acc;
    }, []);
}

/** Map resolved editable fields to the posture PUT body. Pure. */
export function buildPosturePutBody(fields: ResolvedPostureFields): PosturePutBody {
    return {
        side: fields.side,
        lumbar_vertebra_end_plate: fields.lumbarLevel,
        femur_1_head_radius: fields.femur1,
        femur_2_head_radius: fields.femur2,
    };
}

/** True when any of side / lumbar level / femur_1 / femur_2 differs from the persisted posture. */
export function postureFieldsChanged(
    fields: ResolvedPostureFields,
    server: PersistedPostureFields,
): boolean {
    return (
        fields.side !== server.side ||
        fields.lumbarLevel !== server.lumbar_vertebra_end_plate ||
        fields.femur1 !== server.femur_1_head_radius ||
        fields.femur2 !== server.femur_2_head_radius
    );
}

/**
 * Overlay a partial set of updated landmarks onto the existing full set, matched by
 * name. The landmarks PUT endpoint echoes ONLY the landmarks it changed (not the full
 * set), so replacing the local set with the response wholesale would drop every
 * un-edited landmark. This keeps the existing set's membership and order, swapping in
 * each updated landmark by name; updates whose name is not already present are ignored
 * (the displayed landmark set is fixed per posture). Pure — returns a new array,
 * mutates nothing. Generic over the landmark type so the domain stays API-agnostic.
 */
export function mergeLandmarkUpdates<T extends { name: string }>(existing: T[], updates: T[]): T[] {
    const updatesByName = new Map(updates.map((update) => [update.name, update]));
    return existing.map((landmark) => updatesByName.get(landmark.name) ?? landmark);
}
